import { useCallback, useEffect, useRef, useState } from 'react'
import { mdiCheck, mdiContentCopy } from '@mdi/js'
import { NavMdiIcon } from './NavTabIcons'

export function formatMapCoordinateLine(
  point: { lng: number; lat: number },
  label?: string,
): string {
  const coords = `Lat ${point.lat.toFixed(4)}° · Lng ${point.lng.toFixed(4)}°`
  const prefix = label?.trim()
  return prefix ? `${prefix} · ${coords}` : coords
}

/** Google Maps / human-readable order: latitude, then longitude (plain decimals). */
export function formatMapCoordinateClipboard(point: { lng: number; lat: number }): string {
  return `${point.lat}, ${point.lng}`
}

async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

const COPY_CONFIRM_MS = 1600

type MapCoordinateCopyStripProps = {
  label?: string
  lng: number
  lat: number
  className?: string
  /** Fired after coordinates are successfully copied to the clipboard. */
  onCopySuccess?: () => void
}

export function MapCoordinateCopyStrip({
  label,
  lng,
  lat,
  className,
  onCopySuccess,
}: MapCoordinateCopyStripProps) {
  const point = { lng, lat }
  const [copied, setCopied] = useState(false)
  const resetCopiedRef = useRef<number | null>(null)

  useEffect(
    () => () => {
      if (resetCopiedRef.current != null) window.clearTimeout(resetCopiedRef.current)
    },
    [],
  )

  const onCopy = useCallback(async () => {
    const ok = await copyTextToClipboard(formatMapCoordinateClipboard(point))
    if (!ok) return
    onCopySuccess?.()
    setCopied(true)
    if (resetCopiedRef.current != null) window.clearTimeout(resetCopiedRef.current)
    resetCopiedRef.current = window.setTimeout(() => {
      resetCopiedRef.current = null
      setCopied(false)
    }, COPY_CONFIRM_MS)
  }, [lat, lng, onCopySuccess])

  const stripClassName = ['map-pipeline-popup-tap-strip', className].filter(Boolean).join(' ')

  return (
    <div className={stripClassName}>
      <span className="map-pipeline-popup-tap-strip-text">
        {formatMapCoordinateLine(point, label)}
      </span>
      <button
        type="button"
        className="map-pipeline-popup-tap-copy-btn"
        onClick={() => void onCopy()}
        aria-label={copied ? 'Coordinates copied' : 'Copy coordinates'}
        title={copied ? 'Copied' : 'Copy lat, lng for maps'}
      >
        <NavMdiIcon
          path={copied ? mdiCheck : mdiContentCopy}
          size={1.05}
          className="map-pipeline-popup-tap-copy-icon"
        />
      </button>
    </div>
  )
}
